import { useEffect, useRef } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { api } from "../lib/api";

/**
 * Polls /api/orders/admin every 60 s and surfaces a toast (plus a system
 * notification when the tab is hidden) whenever a scheduled order has just
 * crossed its `scheduled_at` and is still not delivered/cancelled.
 *
 * State persistence: `localStorage` key `overdue-notified-{order_id}`. We
 * only fire once per order, even after a full reload, so the operator
 * isn't spammed every minute. Cleared automatically when the order is
 * closed (delivered/cancelled) — we just stop checking it.
 *
 * Mounted from AppShell only when the active user is a company admin.
 */
export function useOverdueOrderListener({ enabled }) {
  const navigate = useNavigate();
  const seededRef = useRef(false);

  useEffect(() => {
    if (!enabled) {
      seededRef.current = false;
      return;
    }
    let cancelled = false;

    const pulse = async () => {
      try {
        const r = await api.get("/orders/admin", {
          params: { page: 1, page_size: 100, status: "active" },
        });
        if (cancelled) return;
        const list = r.data?.items || [];
        const now = Date.now();
        const overdue = list.filter((o) => {
          if (o.when !== "scheduled" || !o.scheduled_at) return false;
          if (o.status === "delivered" || o.status === "cancelled") return false;
          return new Date(o.scheduled_at).getTime() < now;
        });
        // First run: seed localStorage so we don't blast the operator with
        // alerts about already-overdue orders the moment they log in.
        if (!seededRef.current) {
          overdue.forEach((o) => {
            try { localStorage.setItem(`overdue-notified-${o.order_id}`, "1"); } catch { /* ignore */ }
          });
          seededRef.current = true;
          return;
        }
        overdue.forEach((o) => {
          const key = `overdue-notified-${o.order_id}`;
          try {
            if (localStorage.getItem(key)) return;
            localStorage.setItem(key, "1");
          } catch { /* ignore */ }
          fireOverdue(o, navigate);
        });
      } catch (_e) { /* ignore */ }
    };

    const initial = setTimeout(pulse, 3000);
    const interval = setInterval(pulse, 60000);
    return () => {
      cancelled = true;
      clearTimeout(initial);
      clearInterval(interval);
    };
  }, [enabled, navigate]);
}

function fireOverdue(o, navigate) {
  const sixHex = (o.order_id || "").slice(-6).toUpperCase();
  const summary = `${o.client_name || "Cliente"} · pasó la hora programada`;
  toast.error(`Pedido #${sixHex} vencido`, {
    description: summary,
    duration: 12000,
    action: {
      label: "Ver",
      onClick: () => navigate(`/admin/orders/${o.order_id}`),
    },
  });
  if (
    typeof window !== "undefined" &&
    "Notification" in window &&
    Notification.permission === "granted" &&
    document.visibilityState === "hidden"
  ) {
    try {
      const n = new Notification(`Pedido vencido #${sixHex}`, {
        body: summary,
        tag: `overdue-order-${o.order_id}`,
        renotify: true,
      });
      n.onclick = () => {
        window.focus();
        navigate(`/admin/orders/${o.order_id}`);
        n.close();
      };
    } catch (_e) { /* ignore */ }
  }
}
